import { cn, formatPercent } from '@/lib/utils';
import './Confidence.css';

export interface ConfidenceProps {
  /** 0..1 model confidence for an answer. */
  value: number;
  /** Hide the numeric label, leaving only the bar (for dense tables). */
  compact?: boolean;
  className?: string;
}

/** Map a confidence ratio to a qualitative band, paired with a semantic tone. */
function band(value: number): { tone: 'success' | 'warning' | 'danger'; label: string } {
  if (value >= 0.75) return { tone: 'success', label: 'Alta' };
  if (value >= 0.5) return { tone: 'warning', label: 'Média' };
  return { tone: 'danger', label: 'Baixa' };
}

/**
 * Answer confidence as a labelled bar. Never color alone: the band word and the
 * percentage both carry the meaning, so a low-confidence answer is unmistakable
 * and a human knows to verify before trusting it.
 */
export function Confidence({ value, compact = false, className }: ConfidenceProps): JSX.Element {
  const clamped = Math.max(0, Math.min(1, value));
  const { tone, label } = band(clamped);

  return (
    <span className={cn('confidence', `confidence--${tone}`, className)}>
      <span
        className="confidence__track"
        role="meter"
        aria-valuenow={Math.round(clamped * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Confiança ${label.toLowerCase()}, ${formatPercent(clamped)}`}
      >
        <span className="confidence__fill" style={{ width: `${clamped * 100}%` }} />
      </span>
      {!compact && (
        <span className="confidence__label tabular">
          {label} · {formatPercent(clamped)}
        </span>
      )}
    </span>
  );
}
